import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';

import { Database, withLogs } from '@core';

import { mapBaseUiNode, withLimit, type BaseNode } from './uiNodes';

const UI_GRAPH_QUERY = `
    *,
    ui_node_categorizations(ui_node_category_id),
    ui_graphs(*)
` as const;

const UI_GRAPH_CATEGORIZATION_PREFIXES = {
    bodyParts: 'body_part.',
    itemParts: 'item_part.',
};

export type GraphsNode = BaseNode & { categories: GraphsCategories };

type GraphsCategories = {
    graphType: string;
    bodyParts: string[];
    itemParts: string[];
};

const getGraphCategories = (graphType: string, categoryIds: string[]): GraphsCategories => ({
    graphType,
    bodyParts: categoryIds.filter((categoryId) => categoryId.startsWith(UI_GRAPH_CATEGORIZATION_PREFIXES.bodyParts)),
    itemParts: categoryIds.filter((categoryId) => categoryId.startsWith(UI_GRAPH_CATEGORIZATION_PREFIXES.itemParts)),
});

export const fetchUiGraph = withLogs(async (supabase: SupabaseClient<Database>): Promise<GraphsNode[]> => {
    const { data, error } = await withLimit(
        3000,
        supabase.from('ui_nodes').select(UI_GRAPH_QUERY).eq('type', 'graph').order('id', { ascending: true }),
    );

    if (error) {
        console.error('error fetching ui_graphs:', error);
        return [];
    }
    const uiGraph = camelcaseKeys(data, { deep: true });
    return uiGraph.flatMap((uiNode) => {
        const graph = uiNode.uiGraphs;
        if (!graph) return [];

        const categoryIds = uiNode.uiNodeCategorizations.map(({ uiNodeCategoryId }) => uiNodeCategoryId);
        const categories = getGraphCategories(graph.graphType, categoryIds);

        return [{ ...mapBaseUiNode(uiNode), categories }];
    });
}, 'fetchUiGraph');
